import type { CSSProperties } from 'react';

/**
 * Shared design tokens for the Feature 5.3 analytics dashboards.
 *
 * Purely presentational — a cohesive categorical + sequential chart palette,
 * one semantic status vocabulary (good/warn/critical/info/neutral) shared by
 * `StatusChip`, KPI deltas, and every recharts widget, plus common
 * grid/axis/tooltip styling so no chart uses recharts' default rainbow.
 * Scoped to `src/components/analytics/` — does not touch Tailwind config or
 * any other app surface.
 */

/** Categorical series palette — cool-neutral base with one warm accent, ordered for max distinctness at small series counts (bars/legends/department breakdowns). */
export const CHART_CATEGORICAL: readonly string[] = [
  '#2563eb', // blue (primary)
  '#0d9488', // teal
  '#7c3aed', // violet
  '#f59e0b', // amber
  '#0891b2', // cyan
  '#db2777', // pink
  '#65a30d', // green-lime
  '#64748b', // slate (overflow / "other")
];

/** Sequential palette (single-metric intensity — heatmaps, coverage gradients), light to dark. */
export const CHART_SEQUENTIAL: readonly string[] = ['#eff6ff', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#1d4ed8'];

export type SemanticKey = 'good' | 'warn' | 'critical' | 'info' | 'neutral';

/** One semantic vocabulary for pass/fail/attention states, shared by StatusChip, DeltaChip, and chart color maps. */
export const SEMANTIC: Record<SemanticKey, { fg: string; bg: string; ring: string; chart: string; chipClass: string }> = {
  good: { fg: '#047857', bg: '#ecfdf5', ring: '#a7f3d0', chart: '#10b981', chipClass: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' },
  warn: { fg: '#b45309', bg: '#fffbeb', ring: '#fde68a', chart: '#f59e0b', chipClass: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
  critical: { fg: '#be123c', bg: '#fff1f2', ring: '#fecdd3', chart: '#ef4444', chipClass: 'bg-rose-50 text-rose-700 ring-rose-600/20' },
  info: { fg: '#1d4ed8', bg: '#eff6ff', ring: '#bfdbfe', chart: '#3b82f6', chipClass: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  neutral: { fg: '#475569', bg: '#f8fafc', ring: '#e2e8f0', chart: '#94a3b8', chipClass: 'bg-slate-100 text-slate-600 ring-slate-500/10' },
};

/** Maps common workflow/result vocabulary to a semantic key — one source for StatusChip and chart color maps. */
export const STATUS_SEMANTIC: Record<string, SemanticKey> = {
  effective: 'good',
  passed: 'good',
  pass: 'good',
  completed: 'good',
  healthy: 'good',
  low: 'good',
  active: 'good',
  ineffective: 'critical',
  failed: 'critical',
  fail: 'critical',
  critical: 'critical',
  high: 'critical',
  material_weakness: 'critical',
  escalated: 'critical',
  overdue: 'critical',
  warning: 'warn',
  watch: 'warn',
  medium: 'warn',
  med: 'warn',
  significant_deficiency: 'warn',
  repeat: 'warn',
  in_progress: 'info',
  prepared: 'info',
  in_review: 'warn',
  signed_off: 'good',
  tod: 'info',
  toe: 'info',
  control_deficiency: 'info',
  pending: 'neutral',
  draft: 'neutral',
  not_started: 'neutral',
  unrated: 'neutral',
  unclassified: 'neutral',
  other: 'neutral',
};

export function semanticFor(value: string | null | undefined, fallback: SemanticKey = 'neutral'): SemanticKey {
  if (!value) return fallback;
  return STATUS_SEMANTIC[value.toLowerCase()] ?? fallback;
}

/**
 * Display label for a status value. Test-run review statuses get their
 * workflow meaning: `prepared` is waiting for the reviewer, `in_review` means
 * the reviewer sent it back to the tester.
 */
const STATUS_LABELS: Record<string, string> = {
  prepared: 'Awaiting review',
  in_review: 'Sent back',
  signed_off: 'Signed off',
};

export function statusLabel(value: string): string {
  return STATUS_LABELS[value.toLowerCase()] ?? value.replace(/_/g, ' ');
}

/** Convenience: chart hex for a status/result string, e.g. Pie <Cell fill={chartColorFor(row.result)} />. */
export function chartColorFor(value: string | null | undefined, fallback: SemanticKey = 'neutral'): string {
  return SEMANTIC[semanticFor(value, fallback)].chart;
}

export const CHART_GRID_STROKE = '#e2e8f0';
export const CHART_AXIS_LINE = { stroke: '#e2e8f0' };
export const CHART_AXIS_TICK = { fontSize: 11, fill: '#64748b' };

export const CHART_TOOLTIP_STYLE: CSSProperties = {
  fontSize: 12,
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.12), 0 2px 6px -2px rgba(15, 23, 42, 0.08)',
  padding: '8px 12px',
  color: '#1e293b',
};

export const CHART_TOOLTIP_LABEL_STYLE: CSSProperties = {
  fontWeight: 600,
  color: '#0f172a',
  marginBottom: 2,
};

export const CHART_CURSOR_FILL = { fill: 'rgba(148, 163, 184, 0.08)' };

export const CHART_LEGEND_STYLE: CSSProperties = { fontSize: 12, color: '#475569' };

/** Rounded top corners for vertical bars — avoids recharts' default square bars. */
export const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];
